import React, { useState, useMemo } from 'react';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Input from '@amzn/meridian/input';
import Alert from '@amzn/meridian/alert';
import {
  CoreModal,
  CorePageLoader,
  useToggle,
  useFilter,
  useInfiniteGetAll,
} from '@amzn/dots-core-ui';
import { Package, useClient } from '../../api';
import { VersionDetail } from './VersionDetail';
import { VersionForm } from './VersionForm';

export type VersionDetailListProps = {
  p: Package;
};

export function VersionDetailList({ p }: VersionDetailListProps): JSX.Element {
  const { tmsClient } = useClient();
  const {
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    allData,
    refetch,
  } = useInfiniteGetAll('version', tmsClient.versions.getPage, {
    packageId: p.id,
  });

  const [isModalOpen, closeModal, openModal] = useToggle();
  const sortedVersions = useMemo(
    () => allData.slice().sort((a, b) => b.id - a.id),
    [allData]
  );

  const [query, setQuery] = useState('');
  const filteredVersions = useFilter(sortedVersions, query, (c) => c.fileName);

  if (isLoading) {
    return <CorePageLoader />;
  }

  if (error) {
    return (
      <Alert size="medium" type="error">
        Sorry, an error occured while retriving the Versions.
      </Alert>
    );
  }

  return (
    <>
      <Row>
        <Input
          value={query}
          placeholder="Filter Versions..."
          onChange={setQuery}
        />
        <Button type="tertiary" onClick={openModal}>
          Add New
        </Button>
        <CoreModal
          title="Add a New Test Suite"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <VersionForm
            sourceType={p.source}
            onCancel={closeModal}
            onSubmit={async (newVersion): Promise<void> => {
              if (newVersion.file) {
                await tmsClient.versions.createTmsVersion(
                  p.id,
                  newVersion?.file,
                  newVersion.description
                );
              } else if (newVersion.url) {
                await tmsClient.versions.createExternalVersion(
                  p.id,
                  newVersion.url,
                  newVersion.description
                );
              }
              closeModal();
              refetch();
            }}
          />
        </CoreModal>
      </Row>
      {!filteredVersions.length && query && (
        <Alert size="medium" type="informational">
          Sorry, no results for <b>{query}</b>...
        </Alert>
      )}
      {!filteredVersions.length && !query && (
        <Alert size="medium" type="informational">
          Hmm, looks like this Package does not have a Version yet...
          <Button size="small" type="link" onClick={openModal}>
            Try adding one!
          </Button>
        </Alert>
      )}
      {filteredVersions.map((version, i) => (
        <VersionDetail
          key={version.id}
          version={version}
          isLatestVersion={i === 0}
          isArchived={version.archived}
          onDelete={refetch}
          onArchive={refetch}
          onEdit={refetch}
        />
      ))}
      {hasNextPage && (
        <Button
          type="tertiary"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? 'Loading...' : 'Load More'}
        </Button>
      )}
    </>
  );
}
